import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  onMounted,
  onBeforeUnmount,
  ref,
  watch,
  getCurrentInstance,
} from "vue";
import {
  EditTripContactPayload,
  ReservationDetail,
  SharedList,
} from "@/models/dto";
import { phoneFormatFilterInternational } from "@/utils/phone";
import type { Customer } from "@/models/dto/Customer";
import ReservationDetailShareDialog from "@/components/ReservationDetailShareDialog.vue";
import ReservationDetailTripContactForm from "@/components/ReservationDetailTripContactForm.vue";
import ReservationDetailContactInfomationVerifySMS from "@/components/ReservationDetailContactInfomationVerifySMS.vue";
import reservation from "@/services/reservation";
import trip from "@/services/trip";
import { toKebab } from "@/utils/string";
import auth from "../../store/modules/auth";
import { ReservationStatusKey, SplitFeatureFlag } from "@/utils/enum";
import customerService from "@/services/customer"; // renamed to avoid conflict
import { EventBus } from "@/utils/eventBus";
import ContactList from "./ContactList.vue";
import type {
  ContactGroup,
  Contact,
  ContactCurrentUserPermissions,
} from "@/models/dto/ContactGroup";
import reservationDetail from "@/services/reservationDetail";
import { useSplit } from "@/composables/useSplit";
import AddContactToReservation from "./AddContactToReservation.vue";
import { ContactType } from "./utils/helpers";
import ConfirmRemoveContactDialog from "./ConfirmRemoveContactDialog.vue";
import CreateEditContact from "@/components/Contact/CreateEditContact.vue";
import { useDisplay } from "@/composables/useDisplay";


export default /*#__PURE__*/_defineComponent({
  __name: 'ReservationDetailContactInfo',
  props: {
    reservation: null,
    sharedList: null,
    allowUserManagement: { type: Boolean },
    isTripContactModalOpen: { type: Boolean },
    isRiderModalOpen: { type: Boolean },
    isVerifyPhoneModalOpen: { type: Boolean }
  },
  emits: ["refresh-shared-list", "refresh"],
  setup(__props: any, { emit }: { emit: ({
  (e: "refresh-shared-list"): void;
  (e: "refresh"): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  reservation: ReservationDetail;
  sharedList: SharedList;
  allowUserManagement?: boolean;
  isTripContactModalOpen?: boolean;
  isRiderModalOpen?: boolean;
  isVerifyPhoneModalOpen?: boolean;
};

const { proxy } = getCurrentInstance()!;
const t = proxy.$t.bind(proxy);
const { xs } = useDisplay();
const fullscreen = computed(() => xs.value);

// Props


// Emits


// Refs
const isShareReservationDialogOpen = ref(false);
const isTripContactDialogOpen = ref(false);
const tripContactCustomer = ref<Customer | null>(null);
const customerData = ref<Customer | null>(null); // renamed from customer to customerData
const contactGroups = ref<ContactGroup[]>([]);
const currentUserCreatePermissions = ref<ContactCurrentUserPermissions | null>(
  null
);
const isUserRoleManagementEnabled = ref(false);
const isLoading = ref(false);
const showContactDialog = ref(false);
const editingContact = ref<Contact | null>(null);
const showRemoveContactDialog = ref(false);

// Split.io integration
const { isFeatureEnabled } = useSplit();

// Watchers
watch(
  () => props.isTripContactModalOpen,
  (val) => {
    isTripContactDialogOpen.value =
      !showTripContact.value && props.isTripContactModalOpen;
  }
);

watch(
  () => props.isRiderModalOpen,
  (val) => {
    isShareReservationDialogOpen.value = props.isRiderModalOpen;
  }
);

// Computed
const isFinished = computed(
  () => props.reservation?.reservationStatus === ReservationStatusKey.Finished
);

const isUserSMSVerified = computed(() => auth.customer?.smsConfirmed);

const isCustomerSMSEnabled = computed(() => customerData.value?.smsConfirmed);

const isTripContactSMSEnabled = computed(
  () => tripContactCustomer.value?.smsConfirmed
);

const bookingContact = computed(
  (): Contact => ({
    id: customerData.value?.customerId,
    firstName: customerData.value?.firstName,
    lastName: customerData.value?.lastName,
    email: customerData.value?.email,
    phone: customerData.value?.phone,
  })
);

const tripContact = computed(
  (): Contact => ({
    id: tripContactCustomer.value?.customerId,
    firstName: tripContactCustomer.value?.firstName,
    lastName: tripContactCustomer.value?.lastName,
    email: tripContactCustomer.value?.email,
    phone: tripContactCustomer.value?.phone,
  })
);

const showTripContact = computed(
  () =>
    props.reservation?.tripContact?.customerId &&
    props.reservation?.tripContact?.customerId !==
      customerData.value?.customerId
);

const invitedUserContacts = computed((): Contact[] => {
  if (!props.sharedList.length) return [];

  let sharedList = props.sharedList;
  if (!props.allowUserManagement) {
    sharedList = sharedList.filter((user) => user.customerId === auth.userId);
  } else {
    const tripContactId =
      tripContact.value?.id || props.reservation?.tripContact?.customerId;
    sharedList = sharedList.filter(
      (user) =>
        ![tripContactId, bookingContact.value.id].includes(user.customerId)
    );
  }

  return sharedList.map((user) => ({
    id: user.customerId,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    phone: user.phone,
  }));
});

const contacts = computed(() => {
  const contacts = [];
  contacts.push({
    label: t("reservationDetail.contactInformation.BOOKING_CONTACT"),
    list: [bookingContact.value],
  });

  if (showTripContact.value) {
    contacts.push({
      label: t("reservationDetail.contactInformation.TRIP_CONTACT"),
      list: [tripContact.value],
    });
  }

  if (invitedUserContacts.value.length) {
    contacts.push({
      label: t("reservationDetail.contactInformation.RIDERS_WITH_ACCESS"),
      list: invitedUserContacts.value,
    });
  }

  return contacts;
});

// Methods
const refreshContacts = async () => {
  if (isUserRoleManagementEnabled.value) {
    await loadContactGroups();
    editingContact.value = null;
  } else {
    await loadCustomer();
    await loadTripContact();
  }
};

const loadContactGroups = async () => {
  isLoading.value = true;
  try {
    const response = await reservationDetail.getContacts(
      props.reservation.reservationId
    );
    contactGroups.value = response.data.contactGroups;
    currentUserCreatePermissions.value =
      response.data.currentUserCreatePermissions;
  } finally {
    isLoading.value = false;
  }
};

const loadTripContact = async () => {
  if (!props.reservation?.tripContact?.customerId) return;

  const response = await customerService.byId(
    props.reservation.tripContact.customerId
  );
  tripContactCustomer.value = response.data.customer;
};

const loadCustomer = async () => {
  const response = await customerService.byId(
    props.reservation?.customer?.customerId
  );
  customerData.value = response.data.customer;
};

const openTripContactDialog = () => {
  isTripContactDialogOpen.value = true;
};

const remove = async (userId: number) => {
  if (!userId) return;

  if (userId === tripContact.value?.id) {
    const tripId = props.reservation.tripId;
    const payload: EditTripContactPayload = {
      tripContact: {
        customerId: null,
      },
    };
    await trip.editTripContact(tripId, payload);
    tripContactCustomer.value = null;
    emit("refresh");
    return;
  }

  await reservation.unshare(props.reservation.reservationId, userId);
  emit("refresh-shared-list");
};

const isSMSEnabled = (contactTypeLabel: string): boolean => {
  if (contactTypeLabel === "Booking Contact") {
    return isCustomerSMSEnabled.value;
  } else if (contactTypeLabel === "Trip Contact") {
    return isTripContactSMSEnabled.value;
  }
  return false;
};

// Lifecycle hooks
onMounted(async () => {
  isUserRoleManagementEnabled.value = await isFeatureEnabled(
    SplitFeatureFlag.UserRoleManagement
  );
  await refreshContacts();

  EventBus.$on("refresh-contacts", refreshContacts);
  isTripContactDialogOpen.value =
    !showTripContact.value && props.isTripContactModalOpen;
  isShareReservationDialogOpen.value = props.isRiderModalOpen;
});

onBeforeUnmount(() => {
  EventBus.$off("refresh-contacts");
});

const onRemoveContact = async () => {
  editingContact.value = null;
  await refreshContacts();
};

const onUpdateContact = async () => {
  await refreshContacts();
  showContactDialog.value = false;
};

const handleContactDialogInput = (val: boolean) => {
  showContactDialog.value = val;
  if (!val) {
    editingContact.value = null;
  }
};

const handleRemoveDialogInput = (val: boolean) => {
  showRemoveContactDialog.value = val;
};

const handleContactEditClick = (contact: Contact) => {
  editingContact.value = contact;
  showContactDialog.value = true;
};

const handleRemoveContactClick = (contact: Contact) => {
  editingContact.value = contact;
  showRemoveContactDialog.value = true;
  showContactDialog.value = false;
};

const handleRemoveContactCancel = () => {
  showRemoveContactDialog.value = false;
  if (fullscreen.value) {
    showContactDialog.value = true;
  }
};

const contactType = ContactType.Reservation;

return { __sfc: true,proxy, t, xs, fullscreen, props, emit, isShareReservationDialogOpen, isTripContactDialogOpen, tripContactCustomer, customerData, contactGroups, currentUserCreatePermissions, isUserRoleManagementEnabled, isLoading, showContactDialog, editingContact, showRemoveContactDialog, isFeatureEnabled, isFinished, isUserSMSVerified, isCustomerSMSEnabled, isTripContactSMSEnabled, bookingContact, tripContact, showTripContact, invitedUserContacts, contacts, refreshContacts, loadContactGroups, loadTripContact, loadCustomer, openTripContactDialog, remove, isSMSEnabled, onRemoveContact, onUpdateContact, handleContactDialogInput, handleRemoveDialogInput, handleContactEditClick, handleRemoveContactClick, handleRemoveContactCancel, contactType, phoneFormatFilterInternational, ReservationDetailShareDialog, ReservationDetailTripContactForm, ReservationDetailContactInfomationVerifySMS, reservation, toKebab, ContactList, AddContactToReservation, ConfirmRemoveContactDialog, CreateEditContact }
}

})